import { ApolloQueryResult } from '@apollo/client';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { defaultTo, get } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { R400 } from '@atlaskit/theme/colors';
import { EntitlementDetails, LinkedDestinationEntitlement } from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { Permissions } from '@atlassian/cssxp-typescommon';
import { isDeletedEntitlement, isOfferingArchived, isOfferingAtNotice } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_SANDBOX_ENTITLEMENT, sentenceCase } from '@atlassian/cssxp-utils-generic';
import { ApprovalType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalStatus, useTaskList } from '@css-xp/apd-task-manager';
import {
  CopyableText,
  Icon,
  OpenSidebarPayload,
  SummarySectionV2,
  formattedV2,
  isAccess,
  useFeatureToggle,
  useSidebar,
} from '@css-xp/base';
import { isTrialEntitlement } from '../../../../common/utils';
import { SIDEBAR } from '../../../../entitlement-page/src/components/entitlement-page';
import { ViewPricingDetails } from './pricing-section';

interface Props {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

export const UpgradeTrialBanner: React.FC<Props> = ({ entitlement, refetch, linkedEntitlements }) => {
  const upgradeTrial = defaultTo(get(entitlement, 'upgradeTrial'), null);
  const startTimeStamp = entitlement.upgradeTrial?.startTimestamp as string;
  const endTimeStamp = entitlement.upgradeTrial?.endTimestamp as string;
  const totalDaysOnTrial = differenceInCalendarDays(parseISO(endTimeStamp), parseISO(startTimeStamp));
  const { openSidebar } = useSidebar();
  const { showExtendTrial } = entitlement;

  const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isTrial = isTrialEntitlement(entitlement, isStatusPage);
  const isFreePlan = entitlement?.edition && entitlement?.edition.includes('Free');
  const [isEnabled] = useFeatureToggle();
  const isSandbox = isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && entitlement?.isSandbox;

  const { isTrialExtApprovalFlowEnabled } = useApprovalDeskPermissions();
  const { loading: taskListLoading, error: taskListError, hasOpenApprovalRequest, approvalStatus } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !isTrialExtApprovalFlowEnabled || !isTrial,
    approvalType: ApprovalType.TRIAL_EXTENSION,
  });

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const trialExtensionRequestSummary = useMemo(() => {
    // for sandbox or deleted entitlement, return null
    if (isSandbox || isDeletedEntitlement(entitlement)) {
      return null;
    }
    if (taskListLoading) {
      return (
        <div className="trial-extension-footer-loader">
          <LoadingIndicator size="medium" />
        </div>
      );
    }

    if (taskListError) {
      return (
        <div data-testid="trial-extension-request-error" className="trial-extension-request-error">
          <div className="error-toast-icon">
            <ErrorIcon key={'error'} label="Error" primaryColor={R400} size="medium" />
          </div>
          <div className="error-text">Something went wrong</div>
        </div>
      );
    }

    if (hasOpenApprovalRequest) {
      return (
        <>
          {approvalStatus && (
            <div>
              <ApprovalStatus status={approvalStatus} />
            </div>
          )}
          <Link to={{ hash: '#approvals' }} className="inner-text">
            View request
          </Link>
        </>
      );
    } else {
      if (!isFreePlan && showExtendTrial && isAccess(Permissions.SUBSCRIPTION_TRAIL_LENGTH_DAYS_WRITE)) {
        const isExtendAppTrialDisabled =
          entitlement?.isApp && (isOfferingArchived(entitlement) || isOfferingAtNotice(entitlement));
        const isStatusPagePublicTrial = isStatusPage && entitlement?.edition?.toLowerCase().includes('public trial');
        const isExtendTrialDisabled = isExtendAppTrialDisabled || isStatusPagePublicTrial;
        const entitlementDetails = {
          ...entitlement,
          edition: entitlement.upgradeTrial?.offering?.name || entitlement.offering?.name,
          offering: entitlement.upgradeTrial?.offering || entitlement.offering,
        };
        return (
          <Button
            testId="extend-trial-btn"
            appearance="link"
            className="inner-text"
            aria-label="Extend Trial"
            onClick={() => {
              handleSidebarAction({
                sidebarType: SIDEBAR.EXTEND_TRIAL,
                payload: {
                  entitlement: entitlementDetails,
                  refetch,
                },
                sidebarOptions: {
                  isOverlay: true,
                },
              });
            }}
            isDisabled={isExtendTrialDisabled}
          >
            Extend
          </Button>
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOpenApprovalRequest, taskListLoading, taskListError, isFreePlan, approvalStatus, entitlement]);

  const getPricingPlan = useCallback(() => {
    const pricingPlanType = upgradeTrial?.pricingPlan?.type;
    return (
      <div className="entitlement-details-column">
        {pricingPlanType ? (
          <CopyableText>
            <span>{sentenceCase(pricingPlanType)}</span>
          </CopyableText>
        ) : (
          <span data-testid="empty-pricing-plan" className="data-unavailable">
            N/A
          </span>
        )}
      </div>
    );
  }, [entitlement]);

  return (
    <>
      <div data-testId="upgrade-trial-banner" className="upgrade-trail-banner" style={{ backgroundColor: '#DEEBFF' }}>
        <div className="flex-container align-content">
          <Icon type="info" size="medium" isHovered={true} />
          <span>
            The customer is currently trialling a no-cost Premium plan. They will still be charged for their existing
            Standard plan.
          </span>
        </div>
        <SummarySectionV2.SummarySection
          testId="summary"
          className="summary-section-wrapper"
          columns="repeat(auto-fit, minmax(250px, 1fr))"
          styles={{ background: 'transparent', boxShadow: 'none', margin: '0px' }}
        >
          <SummarySectionV2.SummaryGridCalloutItem testId="summary-offering-name">
            <div className="billing-plan billing-cycle title-height">
              <label>Plan and trial period</label>
              <div className="entitlement-details-column">
                <CopyableText>
                  <span>{sentenceCase(upgradeTrial?.offeringName)}</span>
                </CopyableText>
                {startTimeStamp && endTimeStamp ? (
                  <CopyableText>
                    <formattedV2.FormattedDateRange start={startTimeStamp} end={endTimeStamp} />
                  </CopyableText>
                ) : (
                  <span data-testid="empty-upgrade-trial-cycle" className="data-unavailable">
                    N/A
                  </span>
                )}
              </div>
            </div>
          </SummarySectionV2.SummaryGridCalloutItem>
          <SummarySectionV2.SummaryGridCalloutItem testId="summary-pricing-plan">
            <div className="billing-plan billing-cycle title-height">
              <label>Pricing</label>
              {getPricingPlan()}

              <ViewPricingDetails
                entitlement={{
                  ...entitlement,
                  pricingPlan: upgradeTrial?.pricingPlan,
                  offering: {
                    id: upgradeTrial?.offeringId as string,
                    name: upgradeTrial?.offering?.name as string,
                  },
                }}
                refetch={refetch}
                linkedEntitlements={linkedEntitlements}
              />
            </div>
          </SummarySectionV2.SummaryGridCalloutItem>
          <SummarySectionV2.SummaryGridCalloutItem testId="summary-total-days-in-trial">
            <div className="billing-plan billing-cycle title-height" data-testid="summary-total-daye-in-trial-label">
              <label>Total days on trial</label>
              <div className="entitlement-details-column">
                {totalDaysOnTrial ? (
                  <CopyableText>
                    <span>{totalDaysOnTrial}</span>
                  </CopyableText>
                ) : (
                  <span className="data-unavailable">0</span>
                )}
              </div>
            </div>
          </SummarySectionV2.SummaryGridCalloutItem>
          <SummarySectionV2.SummaryGridCalloutItem testId="summary-trial-extensions-request">
            <div
              className="billing-plan billing-cycle title-height"
              data-testid="summary-trial-extensions-request-label"
            >
              <label>Trial extensions</label>
              <div className="entitlement-details-column">{trialExtensionRequestSummary}</div>
            </div>
          </SummarySectionV2.SummaryGridCalloutItem>
          <SummarySectionV2.SummaryGridCalloutItem testId="summary-request">{}</SummarySectionV2.SummaryGridCalloutItem>
        </SummarySectionV2.SummarySection>
      </div>
    </>
  );
};
